import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { InputFile } from "@/components/ui/input-file";
import { ICalendarEvent } from "@/interfaces/dashboard.interface";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addEventSchema, AddEventFormData } from "./schemas";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Check } from "lucide-react";
import { cn } from "@/lib/class-control";

interface AddEventDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (event: Partial<ICalendarEvent>) => void;
}

export function AddEventDialog({ open, onClose, onSubmit }: AddEventDialogProps) {
  const [startDateCalendarOpen, setStartDateCalendarOpen] = useState(false);
  const [endDateCalendarOpen, setEndDateCalendarOpen] = useState(false);

  const form = useForm<AddEventFormData>({
    resolver: zodResolver(addEventSchema),
    defaultValues: {
      title: "",
      description: "",
      date: new Date(),
      startTime: "",
      endDate: new Date(),
      endTime: "",
      ordemDoDia: undefined,
      analiseTecnica: undefined,
      outrosAnexos: undefined,
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        title: "",
        description: "",
        date: new Date(),
        startTime: "",
        endDate: new Date(),
        endTime: "",
        ordemDoDia: undefined,
        analiseTecnica: undefined,
        outrosAnexos: undefined,
      });
    }
  }, [open, form]);

  const handleSubmit = (data: AddEventFormData) => {
    const newEvent: Partial<ICalendarEvent> = {
      id: `event-${Date.now()}`,
      title: data.title,
      description: data.description,
      date: data.date.toISOString().split("T")[0],
      startTime: data.startTime,
      endDate: data.endDate.toISOString().split("T")[0],
      endTime: data.endTime,
      // TODO: Handle file uploads to S3 and store URLs
      // ordemDoDia: uploadedOrdemDoDiaUrl,
      // analiseTecnica: uploadedAnaliseTecnicaUrl,
      // outrosAnexos: uploadedOutrosAnexosUrl,
    };

    onSubmit(newEvent);
    onClose();
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#15203C] border-[#3D3D3D] text-white max-w-[1010px]">
        <DialogHeader>
          <DialogTitle className="text-[24px] font-bold text-white">
            Adicionar Evento
          </DialogTitle>
        </DialogHeader>

        <div className="h-0 border-b border-gray-800/60 w-full" />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-[14px]">
            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[16px] font-bold text-white">
                    Nome do Evento
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite o nome"
                      className="bg-[#15203C] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[16px] font-bold text-white">
                    Descrição do Evento
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Digite o nome"
                      className="bg-[#15203C] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Start Date and Time Row */}
            <div className="grid grid-cols-2 gap-[14px]">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="text-[16px] font-bold text-white">
                      Data de Início
                    </FormLabel>
                    <Popover open={startDateCalendarOpen} onOpenChange={setStartDateCalendarOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] text-white hover:bg-[#0C1427]/80",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 size-4" />
                            {field.value ? formatDate(field.value) : "dd/mm/aaaa"}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#15203C] border-[#3D3D3D]" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setStartDateCalendarOpen(false);
                          }}
                          className="rounded-md"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Hora de Início
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="time"
                        placeholder="00:00"
                        className="bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* End Date and Time Row */}
            <div className="grid grid-cols-2 gap-[14px]">
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="text-[16px] font-bold text-white">
                      Data de Término
                    </FormLabel>
                    <Popover open={endDateCalendarOpen} onOpenChange={setEndDateCalendarOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] text-white hover:bg-[#0C1427]/80",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 size-4" />
                            {field.value ? formatDate(field.value) : "dd/mm/aaaa"}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#15203C] border-[#3D3D3D]" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setEndDateCalendarOpen(false);
                          }}
                          className="rounded-md"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Hora de Término
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="time"
                        placeholder="00:00"
                        className="bg-[#0C1427] border-[#5D5D5D] h-[59px] px-[20px] py-[10px] text-white placeholder:text-[#D1D1D1]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* File Upload Row 1 */}
            <div className="grid grid-cols-2 gap-[29px]">
              <FormField
                control={form.control}
                name="ordemDoDia"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Ordem do Dia
                    </FormLabel>
                    <FormControl>
                      <InputFile field={field} placeholder="Faça upload (JPG, PDF, PNG)" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="analiseTecnica"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[16px] font-bold text-white">
                      Análise Técnica
                    </FormLabel>
                    <FormControl>
                      <InputFile field={field} placeholder="Faça upload (JPG, PDF, PNG)" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* File Upload Row 2 */}
            <FormField
              control={form.control}
              name="outrosAnexos"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[16px] font-bold text-white">
                    Outros Anexos
                  </FormLabel>
                  <FormControl>
                    <InputFile field={field} placeholder="Faça upload (JPG, PDF, PNG)" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex gap-[20px] sm:gap-[20px] mt-[26px]">
              <Button
                type="button"
                onClick={onClose}
                className="bg-[#DC4426] text-[#F5F2FF] hover:bg-[#C33A1F] h-[50px] px-[10px] text-[18px] font-bold"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#13D480] text-[#F5F2FF] hover:bg-[#11BD72] h-[50px] px-[10px] text-[18px] font-bold flex items-center gap-[3px]"
              >
                <Check className="size-6" />
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
